import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { adminApi, api } from '../../lib/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { Tooltip } from '../../components/Tooltip';
import type { AdminOrg, AdminMember, OrgRole, AdminApiKey, ClassifierBundle } from '../../types';

export function OrgDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const { data: orgData, mutate: mutateOrg, error: orgError } = useSWR(
    id ? ['admin/orgs', id] : null,
    () => adminApi.getOrg(id!)
  );
  const { data: memberData, mutate: mutateMembers } = useSWR(
    id ? ['admin/orgs', id, 'members'] : null,
    () => adminApi.getOrgMembers(id!)
  );
  const { data: keyData } = useSWR(
    id ? ['admin/orgs', id, 'api-keys'] : null,
    () => adminApi.getOrgApiKeys(id!)
  );

  if (orgError) {
    return <div className="text-sm text-red-400">Failed to load org: {(orgError as Error).message}</div>;
  }
  if (!orgData) return <div className="text-sm text-gray-500">Loading…</div>;

  const org = orgData.organization;

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Delete '${org.name}'?`,
      message: 'This removes all memberships. API keys keep their dangling org reference.',
      confirmLabel: 'Delete org',
      danger: true,
    });
    if (!ok) return;
    await adminApi.deleteOrg(org._id);
    navigate('/admin/orgs');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/admin/orgs" className="text-xs text-gray-500 hover:text-gray-300">← Organizations</Link>
          <h1 className="text-2xl font-semibold text-gray-100 mt-1">{org.name}</h1>
          <div className="text-sm text-gray-500 mt-1">
            <code className="text-xs">{org.slug}</code> · created {new Date(org.createdAt).toLocaleDateString()}
          </div>
        </div>
        <button onClick={handleDelete} className="px-3 py-1.5 text-sm rounded bg-red-900/40 text-red-300 border border-red-900 hover:bg-red-900/60">
          Delete org
        </button>
      </div>

      <OrgSettingsCard org={org} onSaved={mutateOrg} />

      <BundleAccessCard org={org} onSaved={mutateOrg} />

      <MembersCard orgId={org._id} members={memberData?.members || []} onChanged={mutateMembers} />

      <ApiKeysCard apiKeys={keyData?.apiKeys || []} orgName={org.name} />
    </div>
  );
}

type BundleAccessMode = 'public-only' | 'allowlist' | 'denylist';

function BundleAccessCard({ org, onSaved }: { org: AdminOrg; onSaved: () => void }) {
  // Defaults preserve the legacy "see all public bundles" behavior.
  const [mode, setMode] = useState<BundleAccessMode>(org.bundleAccess?.mode ?? 'public-only');
  const [allowedKeys, setAllowedKeys] = useState<string[]>(org.bundleAccess?.allowedBundleKeys ?? []);
  const [deniedKeys, setDeniedKeys] = useState<string[]>(org.bundleAccess?.deniedBundleKeys ?? []);
  const [defaultBundleKey, setDefaultBundleKey] = useState<string>(org.defaultBundleKey ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync local state when switching between orgs.
  useEffect(() => {
    setMode(org.bundleAccess?.mode ?? 'public-only');
    setAllowedKeys(org.bundleAccess?.allowedBundleKeys ?? []);
    setDeniedKeys(org.bundleAccess?.deniedBundleKeys ?? []);
    setDefaultBundleKey(org.defaultBundleKey ?? '');
  }, [org._id, org.bundleAccess?.mode, org.bundleAccess?.allowedBundleKeys, org.bundleAccess?.deniedBundleKeys, org.defaultBundleKey]);

  // Fetch the full bundle list — needed for the allow/deny checkboxes and the
  // default-bundle selector (rendered in every mode).
  const { data: bundlesData } = useSWR('registry/bundles', () => api.getBundles());
  const bundles = bundlesData?.bundles || [];

  const toggle = (setter: typeof setAllowedKeys) => (key: string) =>
    setter((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  // Bundles eligible to be the org default, steered toward what the org can
  // actually see. Defaults still bypass the gate at request time.
  const defaultCandidates =
    mode === 'allowlist' ? bundles.filter((b) => allowedKeys.includes(b.key))
    : mode === 'denylist' ? bundles.filter((b) => !deniedKeys.includes(b.key))
    : bundles.filter((b) => b.visibility !== 'private');
  const defaultOutsideScope = !!defaultBundleKey && !defaultCandidates.some((b) => b.key === defaultBundleKey);
  const outsideScopeReason =
    mode === 'allowlist' ? "isn't in the allowlist"
    : mode === 'denylist' ? 'is on the denylist'
    : "isn't a public bundle";

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.updateOrg(org._id, {
        bundleAccess: {
          mode,
          allowedBundleKeys: mode === 'allowlist' ? allowedKeys : [],
          deniedBundleKeys: mode === 'denylist' ? deniedKeys : [],
        },
        defaultBundleKey: defaultBundleKey || null,
      });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const MODES: Array<{ value: BundleAccessMode; label: string }> = [
    { value: 'public-only', label: 'Public bundles only' },
    { value: 'allowlist', label: 'Explicit allowlist' },
    { value: 'denylist', label: 'Explicit denylist' },
  ];

  return (
    <div className="bg-gray-900 border border-gray-800 rounded p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm uppercase tracking-wide text-gray-500 flex items-center">
          Bundle access
          <Tooltip text="Controls which classifier bundles this org's API keys can list (GET /v1/bundles) and pass as the `bundle` parameter on /v1/reflections and /v1/batches." />
        </h2>
      </div>

      {/* Access mode */}
      <div className="space-y-2">
        <div className="text-xs text-gray-400 flex items-center">
          Access mode
          <Tooltip text="Public only: sees every public bundle, auto-following the public flag. Allowlist: sees ONLY the checked bundles (everything else hidden, including public). Denylist: sees everything EXCEPT the checked bundles, including private ones — empty denylist = sees all (used for the internal Alphy org)." />
        </div>
        <div className="inline-flex rounded border border-gray-800 bg-gray-950 p-0.5 gap-0.5">
          {MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => setMode(m.value)}
              className={`px-3 py-1.5 text-sm rounded ${mode === m.value ? 'bg-blue-900/60 text-blue-100' : 'text-gray-400 hover:text-gray-200'}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {mode === 'public-only' && (
          <p className="text-xs text-gray-500">
            This org's API keys see every <code>visibility=public</code> bundle, and automatically pick up
            new public bundles. Mirrors the default behavior for keys created before per-org access existed.
          </p>
        )}

        {mode === 'allowlist' && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">
              Only the bundles checked below are visible to this org's API keys — anything unchecked
              is excluded, <strong>including public bundles</strong>. Checking a
              <span className="px-1"><code>private</code></span>
              bundle is an explicit grant. New bundles are <strong>not</strong> auto-included.
            </p>
            <BundleChecklist bundles={bundles} selected={allowedKeys} onToggle={toggle(setAllowedKeys)} />
          </div>
        )}

        {mode === 'denylist' && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">
              This org's API keys see <strong>every</strong> bundle (including <code>private</code> ones and any
              future bundle) <strong>except</strong> the ones checked below. Leave everything unchecked to grant
              access to all bundles — this is how the internal <strong>Alphy</strong> org is configured.
            </p>
            <BundleChecklist bundles={bundles} selected={deniedKeys} onToggle={toggle(setDeniedKeys)} />
          </div>
        )}
      </div>

      {/* Default bundle */}
      <div className="space-y-1.5 border-t border-gray-800 pt-3">
        <div className="text-xs text-gray-400 flex items-center">
          Default bundle
          <Tooltip text="The bundle used when an API request doesn't specify a `bundle`. Applies to /v1/reflections and /v1/batches. Leave as System default to fall back to the platform-wide default bundle." />
        </div>
        <select
          value={defaultBundleKey}
          onChange={(e) => setDefaultBundleKey(e.target.value)}
          className="w-full sm:w-96 bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100"
        >
          <option value="">System default</option>
          {defaultCandidates.map((b) => (
            <option key={b.key} value={b.key}>
              {b.name} ({b.key}){b.isDefault ? ' — global default' : ''}
            </option>
          ))}
          {defaultOutsideScope && (
            <option value={defaultBundleKey}>{defaultBundleKey} (not in this org's access)</option>
          )}
        </select>
        {defaultOutsideScope && (
          <p className="text-xs text-amber-300/80">
            The selected default {outsideScopeReason}. It still works for unspecified requests (org defaults
            bypass the access gate), but callers won't see it in <code className="px-1">GET /v1/bundles</code>.
          </p>
        )}
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}

      <div className="flex justify-end">
        <button
          disabled={busy}
          onClick={submit}
          className="px-3 py-1.5 text-sm rounded bg-blue-900/60 text-blue-100 disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// Checkbox list of bundles shared by the allowlist (checked = allowed) and
// denylist (checked = denied) editors.
function BundleChecklist({
  bundles,
  selected,
  onToggle,
}: {
  bundles: ClassifierBundle[];
  selected: string[];
  onToggle: (key: string) => void;
}) {
  return (
    <div className="max-h-72 overflow-y-auto border border-gray-800 rounded divide-y divide-gray-800/50">
      {bundles.length === 0 ? (
        <div className="px-3 py-2 text-xs text-gray-500">Loading bundles…</div>
      ) : (
        bundles.map((b) => (
          <label key={b._id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-800/40 cursor-pointer">
            <input
              type="checkbox"
              checked={selected.includes(b.key)}
              onChange={() => onToggle(b.key)}
              className="accent-blue-500"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-100 flex items-center gap-2">
                <span className="truncate">{b.name}</span>
                {b.visibility === 'private' && (
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-900/50 text-amber-200 border border-amber-900">
                    private
                  </span>
                )}
                {b.isDefault && (
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 border border-gray-700">
                    global default
                  </span>
                )}
                {!b.isActive && (
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-800 text-gray-500 border border-gray-700">
                    inactive
                  </span>
                )}
              </div>
              <code className="text-xs text-gray-500">{b.key}</code>
            </div>
          </label>
        ))
      )}
    </div>
  );
}

function OrgSettingsCard({ org, onSaved }: { org: AdminOrg; onSaved: () => void }) {
  const [form, setForm] = useState<Partial<AdminOrg>>({
    name: org.name,
    slug: org.slug,
    shouldStoreReflections: org.shouldStoreReflections,
    labelsDisabled: org.labelsDisabled,
    onlyStoreNegativeReflections: org.onlyStoreNegativeReflections,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset when org id changes
  useEffect(() => {
    setForm({
      name: org.name,
      slug: org.slug,
      shouldStoreReflections: org.shouldStoreReflections,
      labelsDisabled: org.labelsDisabled,
      onlyStoreNegativeReflections: org.onlyStoreNegativeReflections,
    });
  }, [org._id, org.name, org.slug, org.shouldStoreReflections, org.labelsDisabled, org.onlyStoreNegativeReflections]);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await adminApi.updateOrg(org._id, form);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded p-4 space-y-3">
      <h2 className="text-sm uppercase tracking-wide text-gray-500">Settings</h2>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" tip="Display name for the organization. Shown here in the console and in the customer portal.">
          <input value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100" />
        </Field>
        <Field label="Slug" tip="URL-safe identifier (lowercase, hyphens, 3+ chars). Used in portal URLs and to reference the org. Changing it breaks existing links.">
          <input value={form.slug ?? ''} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} className="w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100" />
        </Field>
        <Field label="Store reflections" tip="When on, each /v1/reflections analysis is saved to this org's history (the portal Logs tab reads it). When off, requests still run, are billed, and appear in Usage — but the text and result are not persisted.">
          <Toggle value={!!form.shouldStoreReflections} onChange={(v) => setForm({ ...form, shouldStoreReflections: v })} />
        </Field>
        <Field label="Labels disabled (legacy)" tip="Legacy alphy-reflect flag. When on, label classification is suppressed for this org. Retained for migrated orgs — leave off for new orgs.">
          <Toggle value={!!form.labelsDisabled} onChange={(v) => setForm({ ...form, labelsDisabled: v })} />
        </Field>
        <Field label="Only store negative reflections (legacy)" tip="Legacy alphy-reflect flag. When on (and storage is enabled), only reflections flagged harmful are persisted. Retained for migrated orgs.">
          <Toggle value={!!form.onlyStoreNegativeReflections} onChange={(v) => setForm({ ...form, onlyStoreNegativeReflections: v })} />
        </Field>
      </div>
      {error && <div className="text-sm text-red-400">{error}</div>}
      <div className="flex justify-end">
        <button disabled={busy} onClick={submit} className="px-3 py-1.5 text-sm rounded bg-blue-900/60 text-blue-100 disabled:opacity-50">
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function MembersCard({ orgId, members, onChanged }: { orgId: string; members: AdminMember[]; onChanged: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('member');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();

  const add = async () => {
    if (!email) return;
    setBusy(true); setError(null);
    try {
      await adminApi.addOrgMember(orgId, { email: email.trim(), role });
      setEmail('');
      onChanged();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const changeRole = async (userId: string, newRole: OrgRole) => {
    await adminApi.changeOrgMemberRole(orgId, userId, newRole);
    onChanged();
  };

  const remove = async (userId: string, email: string) => {
    const ok = await confirm({
      title: `Remove ${email}?`,
      message: 'They will lose all access scoped to this organization.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    await adminApi.removeOrgMember(orgId, userId);
    onChanged();
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded p-4 space-y-3">
      <h2 className="text-sm uppercase tracking-wide text-gray-500">Members ({members.length})</h2>

      <div className="flex items-center gap-2">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          className="flex-1 bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100"
        />
        <select value={role} onChange={(e) => setRole(e.target.value as OrgRole)} className="bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-100">
          <option value="owner">owner</option>
          <option value="admin">admin</option>
          <option value="member">member</option>
        </select>
        <button disabled={busy || !email} onClick={add} className="px-3 py-1.5 text-sm rounded bg-blue-900/60 text-blue-100 disabled:opacity-50">Add</button>
      </div>
      {error && <div className="text-sm text-red-400">{error}</div>}

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-800 text-gray-400 text-left">
            <th className="px-2 py-1 font-medium">Email</th>
            <th className="px-2 py-1 font-medium">Role</th>
            <th className="px-2 py-1 font-medium">Joined</th>
            <th className="px-2 py-1 font-medium"></th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id} className="border-b border-gray-800/50">
              <td className="px-2 py-1.5 text-gray-100">{m.user?.email || '(deleted user)'}</td>
              <td className="px-2 py-1.5">
                <select
                  value={m.role}
                  onChange={(e) => m.user && changeRole(m.user._id, e.target.value as OrgRole)}
                  className="bg-gray-950 border border-gray-800 rounded px-2 py-0.5 text-xs text-gray-100"
                >
                  <option value="owner">owner</option>
                  <option value="admin">admin</option>
                  <option value="member">member</option>
                </select>
              </td>
              <td className="px-2 py-1.5 text-xs text-gray-500">{new Date(m.joinedAt).toLocaleDateString()}</td>
              <td className="px-2 py-1.5 text-right">
                <button
                  onClick={() => m.user && remove(m.user._id, m.user.email)}
                  className="text-xs text-red-400 hover:text-red-300"
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
          {members.length === 0 && (
            <tr><td colSpan={4} className="px-2 py-4 text-center text-gray-600 text-xs">No members yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ApiKeysCard({ apiKeys, orgName }: { apiKeys: AdminApiKey[]; orgName: string }) {
  const confirm = useConfirm();
  const revoke = async (k: AdminApiKey) => {
    const ok = await confirm({
      title: `Revoke key ${k.prefix}…?`,
      message: `Any client using this key for ${orgName} will start getting 401s immediately. Revocation cannot be undone.`,
      confirmLabel: 'Revoke key',
      danger: true,
    });
    if (!ok) return;
    await adminApi.revokeApiKey(k._id);
    location.reload();
  };
  return (
    <div className="bg-gray-900 border border-gray-800 rounded p-4 space-y-3">
      <h2 className="text-sm uppercase tracking-wide text-gray-500">API keys ({apiKeys.length})</h2>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-800 text-gray-400 text-left">
            <th className="px-2 py-1 font-medium">Prefix</th>
            <th className="px-2 py-1 font-medium">Name</th>
            <th className="px-2 py-1 font-medium">Owner</th>
            <th className="px-2 py-1 font-medium">Env</th>
            <th className="px-2 py-1 font-medium">Status</th>
            <th className="px-2 py-1 font-medium">Last used</th>
            <th className="px-2 py-1 font-medium"></th>
          </tr>
        </thead>
        <tbody>
          {apiKeys.map((k) => {
            const ownerEmail = typeof k.userId === 'object' ? k.userId.email : '—';
            return (
              <tr key={k._id} className="border-b border-gray-800/50">
                <td className="px-2 py-1.5"><code className="text-xs text-gray-300">{k.prefix}</code></td>
                <td className="px-2 py-1.5 text-gray-300">{k.name}</td>
                <td className="px-2 py-1.5 text-gray-400 text-xs">{ownerEmail}</td>
                <td className="px-2 py-1.5 text-gray-400 text-xs">{k.environment}</td>
                <td className="px-2 py-1.5">{k.isActive ? <span className="text-emerald-400 text-xs">active</span> : <span className="text-gray-500 text-xs">revoked</span>}</td>
                <td className="px-2 py-1.5 text-gray-500 text-xs">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}</td>
                <td className="px-2 py-1.5 text-right">
                  {k.isActive && (
                    <button
                      onClick={() => revoke(k)}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          {apiKeys.length === 0 && (
            <tr><td colSpan={7} className="px-2 py-4 text-center text-gray-600 text-xs">No API keys</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, tip, children }: { label: string; tip?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs text-gray-400 mb-1 flex items-center">
        {label}
        {tip && <Tooltip text={tip} />}
      </div>
      {children}
    </label>
  );
}
function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`px-3 py-1.5 rounded text-sm border ${value ? 'bg-emerald-900/40 text-emerald-200 border-emerald-800' : 'bg-gray-800 text-gray-400 border-gray-700'}`}
    >
      {value ? 'on' : 'off'}
    </button>
  );
}
